import { useEffect, useState, useRef, useContext } from 'react';
import { useLocation } from 'react-router';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { useAuth0 } from '@auth0/auth0-react';
import { PromotionTrackingContext } from '@lib/PromotionTrackingContext';
import { isSamePageFlyoutClose } from '@lib/isSamePageFlyoutNavigation';
import useSelector from '@redux/typedHooks';
import { useGTMDataLayer } from '../GTM/useGTMDataLayer';
import { NAFGTMEvent } from '../../../../types/GTM/NAFGTMEvent';
import { globalTabState } from '../useActiveTab';
import { useMembershipStatus } from '../useMembershipStatus';
import { maskLicensePlateNumber } from './maskLicensePlateNumber';
import { getPageReferrer, setLastVirtualPageUrl } from './pageReferrer';
import { useAuth0Token } from '../useAuth0Token';
import { useIsomorphicLayoutEffect } from '../useIsomorphicLayoutEffect';

type PendingPageView = {
  url: string;
  page: string;
  pageUrl: string;
};

export const useVirtualPageViewTracking = () => {
  const location = useLocation();
  const dataLayer = useGTMDataLayer();
  const baseUrl = useSelector((state) => state.application.baseUrl);
  const promotionTracking = useContext(PromotionTrackingContext);
  const setIsVirtualPageViewTracked = promotionTracking?.setIsVirtualPageViewTracked;
  const isVirtualPageViewTrackedRef = promotionTracking?.isVirtualPageViewTrackedRef;
  const pageViewKeyRef = promotionTracking?.pageViewKeyRef;
  const user = useSelector((state) => state.myMembership.customerInformation.data);
  const {
    meta: { fetchState: customerInfoFetchState },
  } = useSelector((state) => state.myMembership.customerInformation);

  const applicationInsights = useAppInsightsContext();
  const { activeTabTitle } = globalTabState;
  const { isMainHouseholdMember, isHouseholdMember } = useMembershipStatus();
  const { isAuthenticated } = useAuth0();
  const { loading: isTokenLoading, simpleToken } = useAuth0Token();
  const [currentTitle, setCurrentTitle] = useState<string>(document?.title);
  const pendingPageViewRef = useRef<PendingPageView | null>(null);
  const hasTrackedUrlRef = useRef<string>('');
  const previousUrlPartsRef = useRef<{ pathname: string; search: string } | null>(null);

  useIsomorphicLayoutEffect(() => {
    const urlParts = { pathname: location.pathname, search: location.search };
    const isFlyoutClose = isSamePageFlyoutClose(previousUrlPartsRef.current, urlParts);
    previousUrlPartsRef.current = urlParts;

    const currentUrl = `${location.pathname}${location.search}`;

    // Closing a flyout returns to a page that was already viewed. Marking it as tracked lets the
    // same flyout count again when it is reopened.
    if (isFlyoutClose) {
      hasTrackedUrlRef.current = currentUrl;
      return;
    }

    setIsVirtualPageViewTracked?.(false);
    if (isVirtualPageViewTrackedRef) {
      isVirtualPageViewTrackedRef.current = false;
    }
    if (pageViewKeyRef) {
      pageViewKeyRef.current = currentUrl;
    }
    if (!dataLayer || !baseUrl) return;

    const maskedPathname = maskLicensePlateNumber(location.pathname);
    const fullUrl = `${baseUrl}${location.pathname}${location.search}`;
    const maskedUrl = maskLicensePlateNumber(fullUrl);

    pendingPageViewRef.current = {
      url: currentUrl,
      page: maskedPathname,
      pageUrl: maskedUrl,
    };
  }, [
    location.pathname,
    location.search,
    dataLayer,
    baseUrl,
    setIsVirtualPageViewTracked,
    isVirtualPageViewTrackedRef,
    pageViewKeyRef,
  ]);

  useEffect(() => {
    const pending = pendingPageViewRef.current;
    if (!pending || !dataLayer) return;

    if (hasTrackedUrlRef.current === pending.url) return;
    if (isTokenLoading) return;

    const hasToken = !!simpleToken;
    const hasUserData = !!user?.customer && !!user?.membership;

    if (hasToken) {
      if (customerInfoFetchState === 'loading') return;
      if (customerInfoFetchState === 'initial' && !hasUserData && isAuthenticated) {
        return;
      }
    }

    const pageTitle = activeTabTitle
      ? `${currentTitle || document.title || 'UNKNOWN'} - ${activeTabTitle}`
      : currentTitle || document.title || 'UNKNOWN';

    const pageReferrer = getPageReferrer();

    const event = {
      event: NAFGTMEvent.virtualPageView,
      page: pending.page,
      pageUrl: pending.pageUrl,
      pageTitle,
      ...(pageReferrer ? { page_referrer: pageReferrer } : {}),
      ...(hasUserData
        ? {
            user_id: user.customerId,
            userType: user.customer.displayRoleName,
            hasMembership: user.membership.isValid,
            isMainMember: isMainHouseholdMember,
            isHouseMember: isHouseholdMember,
            hasRoadSideAssistance: !!user.membership.roadSideAssistance,
          }
        : {}),
    };

    dataLayer.push(event);
    setLastVirtualPageUrl(pending.pageUrl);
    setIsVirtualPageViewTracked?.(true);
    if (isVirtualPageViewTrackedRef) {
      isVirtualPageViewTrackedRef.current = true;
    }

    applicationInsights?.trackPageView({
      name: pageTitle,
      uri: pending.pageUrl,
      properties: {
        tabName: activeTabTitle || null,
      },
    });

    hasTrackedUrlRef.current = pending.url;
    pendingPageViewRef.current = null;
  }, [
    dataLayer,
    user,
    isAuthenticated,
    isTokenLoading,
    simpleToken,
    customerInfoFetchState,
    isMainHouseholdMember,
    isHouseholdMember,
    applicationInsights,
    activeTabTitle,
    currentTitle,
    setIsVirtualPageViewTracked,
    isVirtualPageViewTrackedRef,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const titleElement = document.querySelector('title');
    if (!titleElement) return;

    const observer = new MutationObserver(() => {
      setCurrentTitle(document.title);
    });

    observer.observe(titleElement, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    setCurrentTitle(document.title);
    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, []);
};

/**
 * Wrapper component
 */
export const VirtualPageViewTracker = () => {
  useVirtualPageViewTracking();
  return null;
};
